import { useEffect } from "react";
import { Link, Navigate, NavLink, Outlet, useMatch, useNavigate, useParams } from "react-router-dom";
import { ApiRequestError } from "@mmm/contracts";
import { useQuery } from "../../../api/useQuery.js";
import { useBackTo } from "../../../routes/useBackTo.js";
import { getSubjectOverview } from "../../../api/queries.js";
import { LoadingRows, LoadingSkeleton, QueryStatus } from "../../../components/ui/LoadingPlaceholders.js";
import { StudyWorkspaceActions } from "../../../components/templates/StudyWorkspaceActions.js";
import { PageDateLine } from "../../../components/ui/PageTitle.js";
import { recordRecentPage } from "../../../recents.js";
import type { SubjectWithChildren } from "../../../types.js";
import { ColorDot, ErrorState, Icon, type MessageKey, useT } from "@mmm/ui";
const TABS: Array<{ to: string; label: MessageKey; end?: boolean }> = [
  { to: "overview", label: "nav.overview" },
  { to: "map", label: "nav.map" },
  { to: "tests", label: "nav.tests" },
];

/** Chapter-scoped workspace: the chapter's single map plus its tests.
 * Rename/delete live on the parent (the subject's Overview tab). */
export function ChapterLayout() {
  const t = useT();
  const { subjectId = "", chapterId = "" } = useParams();
  const navigate = useNavigate();
  const backLink = useBackTo();
  const atTests = useMatch("/subjects/:subjectId/chapters/:chapterId/tests") !== null;
  const overview = useQuery(getSubjectOverview, { subjectId });

  useEffect(() => {
    if (overview.error instanceof ApiRequestError && overview.error.code === "notFound") {
      navigate("/dashboard", { replace: true });
    }
  }, [overview.error, navigate]);

  // Record one chapter-level recent entry, regardless of the active tab.
  const subjectTitle = overview.data?.subject.title;
  const subjectColor = overview.data?.subject.color;
  const chapterTitle = overview.data?.chapters.find((c) => c.id === chapterId)?.title;
  useEffect(() => {
    if (!subjectTitle || !subjectColor || !chapterTitle) return;
    recordRecentPage({
      path: `/subjects/${subjectId}/chapters/${chapterId}`,
      title: chapterTitle,
      detail: subjectTitle,
      color: subjectColor,
      subjectId,
    });
  }, [subjectId, chapterId, subjectTitle, subjectColor, chapterTitle]);

  // A chapter missing from the overview means "no such chapter" ONLY once the
  // data is settled. useQuery is stale-while-revalidate, so opening a
  // just-created chapter renders first against the cached, pre-creation
  // overview — redirecting on that would bounce the user straight back out of
  // the chapter they just made (and did, until this guard existed). Wait for
  // the revalidation in flight instead.
  const chapterMissingFromSettledData =
    overview.data !== undefined &&
    !overview.refreshing &&
    !overview.waiting &&
    !overview.data.chapters.some((c) => c.id === chapterId);

  if (chapterMissingFromSettledData) {
    return <Navigate to={`/subjects/${subjectId}`} replace />;
  }

  if (!overview.data) {
    const notFound = overview.error instanceof ApiRequestError && overview.error.code === "notFound";
    // notFound is handled by the redirect effect above — don't flash an
    // ErrorState in the single render before it fires.
    if (overview.error && !overview.waiting && !notFound) {
      return (
        <div>
          <ErrorState title={t("chapters.loadOneFailed")} onRetry={overview.retry} />
        </div>
      );
    }
    return (
      <div>
        <div className="mmm-page-header">
          <div style={{ width: "min(420px, 100%)" }}>
            <LoadingSkeleton height={13} width="160px" />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
              <LoadingSkeleton height={20} width="20px" className="mmm-skeleton--swatch" />
              <LoadingSkeleton height={30} width="72%" />
            </div>
          </div>
        </div>
        <QueryStatus queries={[overview]} />
        <div className="mmm-card" style={{ padding: 20, maxWidth: 640 }}>
          <LoadingRows rows={4} />
        </div>
      </div>
    );
  }

  const subject: SubjectWithChildren = {
    ...overview.data.subject,
    chapters: overview.data.chapters,
    checkItems: overview.data.checkItems,
    events: overview.data.events,
    mindMaps: overview.data.mindMaps,
    aiTests: overview.data.aiTests,
  };

  const chapter = subject.chapters.find((c) => c.id === chapterId);
  // Not settled yet (the guard above already handled the settled case), so
  // show the skeleton rather than redirecting on stale data.
  if (!chapter) {
    return (
      <div>
        <QueryStatus queries={[overview]} />
        <div className="mmm-card" style={{ padding: 20, maxWidth: 640 }}>
          <LoadingRows rows={4} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mmm-page-header">
        <div>
          <PageDateLine>
            {" - "}
            <Link to={`/subjects/${subjectId}`} style={{ color: "var(--mmm-purple)", fontWeight: 600 }}>
              {subject.title}
            </Link>
          </PageDateLine>
          <div className="mmm-title-line">
            {/* Back to wherever you arrived from — the inventory, if that's
                where you clicked through. Falls back to the parent subject,
                which is the right answer when there's no history to honour
                (a bookmark, a refresh, or a link opened in a new tab). */}
            <Link
              className="mmm-title-back-btn"
              to={backLink?.to ?? `/subjects/${subjectId}`}
              aria-label={backLink?.label ?? t("common.backToItem", { title: subject.title })}
            >
              <Icon name="collapse" />
            </Link>
            <ColorDot color={subject.color} size="md" square />
            <h1 className="mmm-page-title">{chapter.title}</h1>
          </div>
        </div>
      </div>
      <QueryStatus queries={[overview]} />

      <div className="mmm-workspace-tabs-row">
        <div className="mmm-workspace-tabs">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to || "map"}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `mmm-workspace-tab ${isActive ? "is-active" : ""}`.trim()}
            >
              {t(tab.label)}
            </NavLink>
          ))}
        </div>

        <StudyWorkspaceActions key={chapter.id} subject={subject} chapter={chapter} tests={atTests} />
      </div>

      <Outlet context={{ subject, chapter }} />
    </div>
  );
}
